import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import {
  getOnboardingDetailListSagaAction,
  onboardingFormSagaActionTypes,
  updateProjectNameSagaAction,
} from "./saga-actions";
import { onboardingFormSliceAction } from "../../slices/onboarding-form";
import {
  OnboardingFormDetails,
  OnboardingListDetails,
} from "../../slices/onboarding-form/Types";

// ----------------------------------------
// API
type getOnboardingDetailListAPIResponseType = {
  success: boolean;
  message: string;
  data: OnboardingListDetails[];
};
const getOnboardingDetailListAPI = async (partnerID: number) => {
  const response = await axiosRestInstance.get<
    AxiosResponse<getOnboardingDetailListAPIResponseType>
  >(`/onboarding/partner/${partnerID}`);
  return response.data;
};

type onboardingOperationResponse = {
  success: boolean;
  message: string;
};

const addOnboardingDetailAPI = async (payload: OnboardingFormDetails) => {
    // Use FormData when files are present
    const formData = new FormData();
    
    // Append file fields only if they are File objects
    if (payload.nocf_reader_document instanceof File) {
      formData.append('nocf_reader_document', payload.nocf_reader_document);
    }
    if (payload.nocf_licenses_document instanceof File) {
      formData.append('nocf_licenses_document', payload.nocf_licenses_document);
    }
    
    // Append all other fields as form fields (so they appear in req.body)
    // Exclude file fields if they are File objects (we already appended them above)
    Object.keys(payload).forEach((key) => {
      const value = payload[key as keyof OnboardingFormDetails];
      
      // Skip file fields if they are File objects (already appended above)
      if (
        (key === 'nocf_reader_document' || key === 'nocf_licenses_document') &&
        value instanceof File
      ) {
        return;
      }
      
      // Skip null/undefined values
      if (value === null || value === undefined) {
        return;
      }
      
      // For nested objects and arrays (cert, preprod, prod, integration_ids), stringify them
      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });

    const response = await axiosRestInstance.post<
      AxiosResponse<onboardingOperationResponse>
    >(`/onboarding/${payload.partner_id}`, formData);
    return response.data;
};

const updateOnboardingDetailAPI = async (payload: OnboardingFormDetails) => {
    const formData = new FormData();
    
    // Append file fields only if they are File objects
    if (payload.nocf_reader_document instanceof File) {
      formData.append('nocf_reader_document', payload.nocf_reader_document);
    }
    if (payload.nocf_licenses_document instanceof File) {
      formData.append('nocf_licenses_document', payload.nocf_licenses_document);
    }
    
    // Append all other fields as form fields (so they appear in req.body)
    // Exclude file fields if they are File objects (we already appended them above)
    Object.keys(payload).forEach((key) => {
      const value = payload[key as keyof OnboardingFormDetails];
      
      // Skip file fields if they are File objects (already appended above)
      if (
        (key === 'nocf_reader_document' || key === 'nocf_licenses_document') &&
        value instanceof File
      ) {
        return;
      }
      
      // Skip null/undefined values
      if (value === null || value === undefined) {
        return;
      }
      
      // For nested objects and arrays (cert, preprod, prod, integration_ids), stringify them
      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });

    // Axios will automatically set Content-Type with boundary for FormData
    const response = await axiosRestInstance.put<
      AxiosResponse<onboardingOperationResponse>
    >(`/onboarding/${payload.ID}`, formData);
    return response.data;
};

const deleteOnboardingDetailAPI = async (ID: number) => {
  const response = await axiosRestInstance.delete<
    AxiosResponse<onboardingOperationResponse>
  >(`/onboarding/${ID}`);
  return response.data;
};

// API for getting onboarding detail by ID
type getOnboardingDetailByIdAPIResponseType = {
  success: boolean;
  message: string;
  data: OnboardingFormDetails[];
};
const getOnboardingDetailByIdAPI = async (onboardingID: number) => {
  const response = await axiosRestInstance.get<
    AxiosResponse<getOnboardingDetailByIdAPIResponseType>
  >(`/onboarding/${onboardingID}`);
  return response.data;
};

// API for updating project name
type updateProjectNameAPIResponseType = {
  success: boolean;
  message: string;
};
const updateProjectNameAPI = async (payload: {
  old_project_name: string;
  new_project_name: string;
}) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<updateProjectNameAPIResponseType>
  >(`/onboarding/updateProjectName`, payload);
  return response.data;
};

// API for getting file view URL
export type getFileViewUrlAPIResponseType = {
  success?: boolean;
  data?: {
    url: string;
  };
  url?: string;
  message?: string;
};
export const getFileViewUrlAPI = async (fileKey: string, fileType: string) => {
  const encodedFileKey = encodeURIComponent(fileKey);
  const response = await axiosRestInstance.get<
    AxiosResponse<getFileViewUrlAPIResponseType>
  >(`/file/url?fileKey=${encodedFileKey}&type=${fileType}&source=project`);
  return response.data;
};

const downloadFileAPI = async (fileKey: string) => {
  const encodedFileKey = encodeURIComponent(fileKey);
  return axiosRestInstance.get<Blob>(
    `/file/url?fileKey=${encodedFileKey}&type=download&source=project`,
    { responseType: "blob" }
  );
};

const getFileNameFromKey = (fileKey: string): string => {
  const parts = fileKey.split("/");
  return parts[parts.length - 1] || "download";
};

// ----------------------------------------
// Sagas

function* getOnboardingDetailListSaga(action: {
  type: string;
  partnerID: number;
}) {
  try {
    yield put(onboardingFormSliceAction.setOnboardingDetailListLoading(true));
    const response: getOnboardingDetailListAPIResponseType = yield call(
      getOnboardingDetailListAPI,
      action.partnerID
    );
    yield put(
      onboardingFormSliceAction.setOnboardingDetailList(
        response.data?.reverse() || []
      )
    );
  } catch (e) {
    console.log(e);
  } finally {
    yield put(onboardingFormSliceAction.setOnboardingDetailListLoading(false));
  }
}

function* addOnboardingDetailSaga(action: {
  type: string;
  payload: OnboardingFormDetails;
}) {
  try {
    yield put(onboardingFormSliceAction.setAddOnboardingDetailLoading(true));
    const response: onboardingOperationResponse = yield call(
      addOnboardingDetailAPI,
      action.payload
    );
    yield put(onboardingFormSliceAction.setAddOnboardingDetailStatus(true));
    yield put(
      onboardingFormSliceAction.setAddOnboardingDetailMessage(response.message)
    );
  } catch (e: any) {
    yield put(onboardingFormSliceAction.setAddOnboardingDetailStatus(false));
    yield put(
      onboardingFormSliceAction.setAddOnboardingDetailMessage(
        e?.response?.data?.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(onboardingFormSliceAction.setAddOnboardingDetailLoading(false));
  }
}

function* updateOnboardingDetailSaga(action: {
  type: string;
  payload: OnboardingFormDetails;
}) {
  try {
    yield put(onboardingFormSliceAction.setUpdateOnboardingDetailLoading(true));
    yield put(
      onboardingFormSliceAction.setUpdateOnboardingConfirmationRequired(false)
    );
    const response: onboardingOperationResponse = yield call(
      updateOnboardingDetailAPI,
      action.payload
    );
    yield put(onboardingFormSliceAction.setUpdateOnboardingDetailStatus(true));
    yield put(
      onboardingFormSliceAction.setUpdateOnboardingDetailMessage(
        response.message
      )
    );
  } catch (e: any) {
    const responseData = e?.response?.data;
    const confirmationRequired =
      e?.response?.status === 409 &&
      responseData?.confirmation_required === true;

    if (confirmationRequired) {
      yield put(
        onboardingFormSliceAction.setUpdateOnboardingDetailMessage(
          responseData?.message ||
            "Selected integrations already exist for this project. Do you want to continue?"
        )
      );
      yield put(
        onboardingFormSliceAction.setUpdateOnboardingConfirmationRequired(true)
      );
      return;
    }

    yield put(onboardingFormSliceAction.setUpdateOnboardingDetailStatus(false));
    yield put(
      onboardingFormSliceAction.setUpdateOnboardingDetailMessage(
        responseData?.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(
      onboardingFormSliceAction.setUpdateOnboardingDetailLoading(false)
    );
  }
}

function* deleteOnboardingDetailSaga(action: {
  type: string;
  payload: {
    ID: number;
    partnerID: number;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
}) {
  try {
    yield put(onboardingFormSliceAction.setDeleteOnboardingDetailLoading(true));
    const response: onboardingOperationResponse = yield call(
      deleteOnboardingDetailAPI,
      action.payload.ID
    );
    yield put(onboardingFormSliceAction.setDeleteOnboardingDetailStatus(true));
    yield put(getOnboardingDetailListSagaAction(action.payload.partnerID));
    action.payload.setModalOpen(false);
  } catch (e) {
    yield put(onboardingFormSliceAction.setDeleteOnboardingDetailStatus(false));
    console.log(e);
  } finally {
    yield put(
      onboardingFormSliceAction.setDeleteOnboardingDetailLoading(false)
    );
  }
}

// Saga for getting onboarding detail by ID
function* getOnboardingDetailByIdSaga(action: {
  type: string;
  onboardingID: number;
}) {
  try {
    yield put(onboardingFormSliceAction.setOnboardingDetailByIdLoading(true));
    const response: getOnboardingDetailByIdAPIResponseType = yield call(
      getOnboardingDetailByIdAPI,
      action.onboardingID
    );
    yield put(
      onboardingFormSliceAction.setOnboardingDetailById(
        response.data?.[0] || null
      )
    );
  } catch (e) {
    console.log(e);
  } finally {
    yield put(onboardingFormSliceAction.setOnboardingDetailByIdLoading(false));
  }
}

// Saga for updating project name
function* updateProjectNameSaga(action: {
  type: string;
  payload: {
    old_project_name: string;
    new_project_name: string;
    setEditProjectName: React.Dispatch<React.SetStateAction<boolean>>;
  };
}) {
  try {
    yield put(onboardingFormSliceAction.setUpdateProjectNameLoading(true));
    const response: updateProjectNameAPIResponseType = yield call(
      updateProjectNameAPI,
      {
        old_project_name: action.payload.old_project_name,
        new_project_name: action.payload.new_project_name,
      }
    );
    yield put(onboardingFormSliceAction.setUpdateProjectNameStatus(true));
    yield put(
      onboardingFormSliceAction.setUpdateProjectNameMessage(response.message)
    );
    // Update the project name in the Redux slice
    yield put(onboardingFormSliceAction.updateProjectNameInList({
      old_project_name: action.payload.old_project_name,
      new_project_name: action.payload.new_project_name
    }));
    // Set setEditProjectName to false after successful API call
    action.payload.setEditProjectName(false);
  } catch (e: any) {
    yield put(onboardingFormSliceAction.setUpdateProjectNameStatus(false));
    yield put(
      onboardingFormSliceAction.setUpdateProjectNameMessage(
        e?.response?.data?.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(onboardingFormSliceAction.setUpdateProjectNameLoading(false));
  }
}

// Saga for getting file view URL / downloading file
function* getFileViewUrlSaga(action: {
  type: string;
  fileKey: string;
  fileType: string;
}) {
  try {
    if (action.fileType === "download") {
      const response: AxiosResponse<Blob> = yield call(
        downloadFileAPI,
        action.fileKey
      );
      const blob = response?.data;
      if (!blob) {
        console.error("Failed to download file: empty response");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getFileNameFromKey(action.fileKey);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    }

    const response: getFileViewUrlAPIResponseType = yield call(
      getFileViewUrlAPI,
      action.fileKey,
      action.fileType
    );
    // Handle different possible response structures
    let url: string | undefined;
    if (response.data?.url) {
      url = response.data.url;
    } else if (response.url) {
      url = response.url;
    }
    
    if (url) {
      window.open(url, "_blank");
    } else {
      console.error("Failed to get file URL:", response.message || "Unknown error");
    }
  } catch (e: any) {
    console.error("Error getting file view URL:", e?.response?.data?.message || e.message || "Something went wrong");
  }
}

// ----------------------------------------
// Root Saga

function* onboardingFormSaga() {
  yield takeLatest(
    onboardingFormSagaActionTypes.callGetOnboardingDetailListSaga,
    getOnboardingDetailListSaga
  );
  yield takeLatest(
    onboardingFormSagaActionTypes.callAddOnboardingDetailSaga,
    addOnboardingDetailSaga
  );
  yield takeLatest(
    onboardingFormSagaActionTypes.callUpdateOnboardingDetailSaga,
    updateOnboardingDetailSaga
  );
  yield takeLatest(
    onboardingFormSagaActionTypes.callDeleteOnboardingDetailSaga,
    deleteOnboardingDetailSaga
  );
  yield takeLatest(
    onboardingFormSagaActionTypes.callGetOnboardingDetailByIdSaga,
    getOnboardingDetailByIdSaga
  );
  yield takeLatest(
    onboardingFormSagaActionTypes.callUpdateProjectNameSaga,
    updateProjectNameSaga
  );
  yield takeLatest(
    onboardingFormSagaActionTypes.callGetFileViewUrlSaga,
    getFileViewUrlSaga
  );
}

export default onboardingFormSaga;
